import { useQuery } from 'react-query';
import { predictionWsAxiosApi } from 'src/Config/axiosApi';
import { PositionsGroupedByPair } from '../types';

interface PositionStats {
  total: number;
  open: number;
  closed: number;
  expired: number;
  totalInvested: number;
  totalUnrealizedPnl: number;
}

export interface PositionsSnapshotResponse {
  grouped: PositionsGroupedByPair[];
  stats: PositionStats | null;
  timestamp: number;
}

/**
 * Seeds the Positions page from the prediction-websocket REST endpoint.
 *
 * The WS `positions_snapshot` event the gateway emits inside
 * `handleConnection` races against React's effect timing — the same race
 * that hides Opportunities on first load (see `useOpportunitiesSnapshot`).
 * Fetching the snapshot over REST closes the race; WS `positions_update`
 * stays as the live channel and overwrites whatever the REST seed put in.
 *
 * Returns the same enriched payload the gateway broadcasts — open
 * positions already carry `topBid`/`topAsk` stamped by
 * `enrichPositionsWithMarketData` in prediction-websocket.
 */
export function usePositionsSnapshot(enabled: boolean) {
  return useQuery<PositionsSnapshotResponse>(
    ['prediction-positions-snapshot'],
    async () => {
      const { data } =
        await predictionWsAxiosApi.get<PositionsSnapshotResponse>(
          '/predictions/positions',
        );
      return data;
    },
    {
      enabled,
      staleTime: 0,
      cacheTime: 0,
      refetchOnWindowFocus: false,
    },
  );
}
